import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { getArticleBySlug, getRelatedArticles } from '../blog/articles';
import SEOHead from '../components/SEOHead';

const BASE_URL = 'https://analizaprawna.eu';

export default function BlogArticlePage() {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const relatedArticles = getRelatedArticles(article);
  const articleUrl = `${BASE_URL}/blog/${article.slug}`;

  const jsonLd = [
    {
      '@type': 'Article',
      headline: article.title,
      description: article.metaDescription,
      url: articleUrl,
      datePublished: article.publishedAt,
      dateModified: article.updatedAt,
      author: {
        '@type': 'Organization',
        name: article.author,
        url: BASE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'AnalizaPrawna',
        url: BASE_URL,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl,
      },
      inLanguage: 'pl',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Strona główna', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} | AnalizaPrawna`}
        description={article.metaDescription}
        canonicalUrl={articleUrl}
        ogType="article"
        article={{
          publishedTime: article.publishedAt,
          modifiedTime: article.updatedAt,
          author: article.author,
        }}
        jsonLd={jsonLd}
      />

      <article className="max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm text-slate-500 dark:text-slate-400">
          <Link to="/blog" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
            Blog
          </Link>
          <span className="mx-2">›</span>
          <span className="text-slate-700 dark:text-slate-300">{article.title}</span>
        </nav>

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
            <time dateTime={article.publishedAt}>
              {new Date(article.publishedAt).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
            <span>·</span>
            <span>{article.readingTimeMinutes} min czytania</span>
            <span>·</span>
            <span>{article.author}</span>
          </div>
        </header>

        {/* Content */}
        <div className="blog-content mb-12">
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 sm:p-8 mb-12 border border-blue-200 dark:border-blue-800">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
            Sprawdź orzeczenia sądów w Twojej sprawie
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-5">
            Nasza wyszukiwarka AI przeanalizuje ponad 500 000 orzeczeń polskich sądów i znajdzie te najbardziej adekwatne do Twojej sytuacji.
          </p>
          <Link
            to={`/search?q=${encodeURIComponent(article.searchQuery)}`}
            className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Wyszukaj powiązane orzeczenia
          </Link>
        </div>

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
              Powiązane artykuły
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="group p-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug">
                    {related.title}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                    {related.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
